import {
  useApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
  useDisableApiKey,
  useEnableApiKey,
} from '@hooks/useApiKeys'
import { useAuth } from '@hooks/useAuth'
import { toast } from '@lib/toasts'
import { ApiKeys } from 'components/api-keys'
import { useTranslation } from 'react-i18next'
import { Layout } from '../../components/Layout'
import { useSettingsOutletContext } from '../Settings'
import * as Sentry from '@sentry/react'
import { ApiKeyImperativeHandle, ApiKeyModal } from './ApiKeyModal'
import { useRef } from 'react'
import { Button } from 'components/buttons'
import { Banner } from 'components/notifications'

export const MyApiKeys = () => {
  const { t } = useTranslation()
  const { dirty, pending, save } = useSettingsOutletContext()
  const { user: signedInUser } = useAuth()
  const apiKeyModal = useRef<ApiKeyImperativeHandle>(null)

  if (!signedInUser) {
    throw new Error(
      'Unreachable: My API Keys should not be rendered if user is not signed in',
    )
  }

  const { data: keys = [], isFetching } = useApiKeys(signedInUser.id)
  const createKeyMutation = useCreateApiKey(signedInUser.id)
  const disableKeyMutation = useDisableApiKey(signedInUser.id)
  const enableKeyMutation = useEnableApiKey(signedInUser.id)
  const removeKeyMutation = useDeleteApiKey(signedInUser.id)

  const apiKeys = keys.map((key) => ({
    ...key,
    label: key.maskedToken,
    status: key.enabled ? ('active' as const) : ('disabled' as const),
  }))

  const enableKey = async (keyId: number) => {
    try {
      await enableKeyMutation.mutateAsync(keyId)
      toast({
        variant: 'success',
        title: t('common.success'),
        content: t('features.settings.apiKeyEnabled'),
      })
    } catch (e) {
      Sentry.captureException(e)
      toast({
        variant: 'error',
        title: t('common.error'),
        content: t('features.settings.unableToEnableApiKey'),
      })
    }
  }

  const disableKey = async (keyId: number) => {
    try {
      await disableKeyMutation.mutateAsync(keyId)
      toast({
        variant: 'success',
        title: t('common.success'),
        content: t('features.settings.apiKeyDisabled'),
      })
    } catch (e) {
      Sentry.captureException(e)
      toast({
        variant: 'error',
        title: t('common.error'),
        content: t('features.settings.unableToDisableApiKey'),
      })
    }
  }

  const removeKey = async (keyId: number) => {
    try {
      await removeKeyMutation.mutateAsync(keyId)
      toast({
        variant: 'success',
        title: t('common.success'),
        content: t('features.settings.apiKeyDeleted'),
      })
    } catch (e) {
      Sentry.captureException(e)
      toast({
        variant: 'error',
        title: t('common.error'),
        content: t('features.settings.unableToDeleteApiKey'),
      })
    }
  }

  const beginCreatingApiKey = async () => {
    try {
      if (!apiKeyModal.current) throw new Error('ApiKeyModal ref is not set')

      apiKeyModal.current.open()
      await createKeyMutation.mutateAsync()
    } catch (e) {
      Sentry.captureException(e)
      toast({
        variant: 'error',
        title: t('common.error'),
        content: t('features.settings.unableToCreateApiKey'),
      })
    }
  }

  return (
    <>
      <Layout
        title={t('features.settings.myApiKeys')}
        subtitle={
          <div className="flex flex-col gap-2">
            <span>{t('features.settings.apiKeysBestPractice')}</span>
            <span className="font-bold">
              {t(
                'features.settings.apiKeysMustBeDisabledBeforeTheyCanBeDeleted',
              )}
            </span>
            <Button
              onClick={() => void beginCreatingApiKey()}
              className="max-w-max"
              disabled={
                createKeyMutation.isLoading ||
                !signedInUser.canSelfProvisionApiAccess
              }
              loading={createKeyMutation.isLoading}
            >
              {t('features.settings.createApiKey')}
            </Button>
          </div>
        }
        onSaveButtonClick={save}
        saveButtonDisabled={!dirty || pending}
      >
        <>
          {signedInUser.canSelfProvisionApiAccess ? (
            <ApiKeys
              keys={apiKeys}
              pending={isFetching}
              onCreateKey={() => void beginCreatingApiKey()}
              onDisable={(keyId) => void disableKey(keyId)}
              onEnable={(keyId) => void enableKey(keyId)}
              onRemove={(keyId) => void removeKey(keyId)}
            />
          ) : (
            <Banner
              dismissable={false}
              variant="alert"
              title={t('features.settings.apiAccessDisabled')}
              content={
                <>
                  {t('features.settings.apiAccessNotEnabled')}{' '}
                  <a
                    href={import.meta.env.VITE_TIME_LOGGER_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {import.meta.env.VITE_TIME_LOGGER_SUPPORT_LABEL}
                  </a>
                </>
              }
            />
          )}
        </>
      </Layout>
      <ApiKeyModal ref={apiKeyModal} token={createKeyMutation.data?.token} />
    </>
  )
}
